/**
 * Simple logging framework for the application
 * Supports different log levels and can be configured for development/production
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix?: string;
}

class Logger {
  private config: LoggerConfig;

  constructor(config: LoggerConfig) {
    this.config = config;
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Check if a log level should be logged
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;

    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }

  /**
   * Format log message with prefix
   */
  private formatMessage(level: LogLevel, message: string, ...args: unknown[]): [string, ...unknown[]] {
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    const levelPrefix = `[${level.toUpperCase()}]`;
    const formattedMessage = `${prefix}${levelPrefix} ${message}`;
    return [formattedMessage, ...args];
  }

  /**
   * Log debug message
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...this.formatMessage('debug', message, ...args));
    }
  }

  /**
   * Log info message
   */
  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.log(...this.formatMessage('info', message, ...args));
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('warn', message, ...args));
    }
  }

  /**
   * Log error message
   */
  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatMessage('error', message, ...args));
    }
  }
}

/**
 * Create logger instance with default configuration
 * In development: all logs enabled
 * In production: only warn and error enabled
 */
function createLogger(prefix?: string): Logger {
  const isDev = import.meta.env.DEV;
  
  return new Logger({
    enabled: true,
    level: isDev ? 'debug' : 'warn',
    prefix
  });
}

/**
 * Default logger instance
 */
export const logger = createLogger();

/**
 * Create a logger with a specific prefix
 */
export function createLoggerWithPrefix(prefix: string): Logger {
  return createLogger(prefix);
}

/**
 * Configure the default logger
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
  logger.configure(config);
}
